import * as React from 'react';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import styled from 'styled-components';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import { BUTTON_CONFIG, ButtonType } from './JRCButtonConfig';
import { ForwardedRef } from 'react';
import { DataCy } from '../../types/dataAttributes';
import { DashedFocusBorder, DashedFocusBorderProps } from '../Common/DashedFocusBorder';
import { JRCConditionalTooltip } from '../Common/JRCConditionalWrapper';
import { JRCTooltipProps } from '../JRCTooltip/JRCTooltip';
import { useIntl } from 'react-intl';
import { SIZES } from '../JRCTypography/JRCTypography.styles';

export type ButtonColors = 'primary' | 'valid' | 'danger' | 'secondary' | 'cancel' | 'neutral' | 'white';

export type JRCButtonProps = React.ComponentPropsWithoutRef<'button'> &
    DataCy & {
        /** Color of the background of the button */
        color?: ButtonColors;
        themeCustom?: ButtonType;
        /** Indicates loading of content on the button */
        loader?: boolean;
        /** This Boolean attribute specifies that the button should have input focus when the page loads */
        autofocus?: boolean;
        /** Defines the value associated with the button’s name when it’s submitted with the form data */
        value?: string;
        /** Enhance your button with an icon */
        icon?: string;
        /** set the icon at the right of the button */
        iconRight?: boolean;
        /** The variant is used to define what type of action the button will take */
        variant?: 'contained' | 'outlined' | 'link';
        /** @deprecated CSS float attribute */
        float?: 'left' | 'right';
        /** CSS min-width attribute */
        minWidth?: string;
        /** has license. If false, the button will be disabled with a license tooltip. Default to true */
        hasLicense?: boolean;
        /** tooltip tooltip props */
        tooltip?: Omit<JRCTooltipProps, 'children'>;
        /** label, optional, replaces children */
        label?: string;
        /** Disable margins **/
        noMargin?: boolean;
        size?: 'm' | 's';
    };

type StyledButtonProps = { $themeButton: ButtonType; hasLabel: boolean } & JRCButtonProps & DashedFocusBorderProps;

/**
 * Button styling. May be used as a <a> tag
 */
export const Button = styled.button.attrs<StyledButtonProps>((props) => ({ ...props }))<StyledButtonProps>`
    float: ${(props) => props.float};
    min-width: ${(props) => props.minWidth};
    display: inline-flex;
    ${(props) => props.iconRight && 'flex-direction: row-reverse'};
    justify-content: center;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : props.loader ? 'wait' : 'pointer')};
    align-items: center;
    color: ${(props) => props.theme.color[props.$themeButton.color]};
    height: ${({ size }) => (size === 's' ? 25 : 40)}px;
    margin: ${(props) => (props.noMargin ? 0 : '0.2rem')};
    outline: none;
    border-radius: ${({ size }) => (size === 'm' ? 8 : 6)}px;
    padding: ${(props) => (props.hasLabel ? (props.size === 'm' ? '8px 16px' : '4px 8px') : '8px 10px')};
    background-color: ${(props) => props.theme.color[props.$themeButton.background]};
    box-sizing: border-box;
    border: 2px solid ${(props) => props.theme.color[props.$themeButton.border] ?? props.$themeButton.border};
    transition: color 0.5s, background-color 0.5s, border 0.5s;
    text-decoration: none;

    &:hover {
        text-decoration: ${(props) => props.$themeButton.hover['text-decoration'] ?? 'none'};
        background-color: ${(props) => props.theme.color[props.$themeButton.hover.background]};
        border-color: ${(props) => props.theme.color[props.$themeButton.hover.border]};
        color: ${(props) => props.theme.color[props.$themeButton.color]};
    }

    &:active {
        text-decoration: ${(props) => props.$themeButton.active['text-decoration'] ?? 'none'};
        background-color: ${(props) => props.theme.color[props.$themeButton.active.background]};
        border-color: ${(props) => props.theme.color[props.$themeButton.active.border]};
    }

    ${(props) => DashedFocusBorder({ color: props.$themeButton.color })}

    & > i,
    & > div {
        ${({ hasLabel }) => !hasLabel && 'margin: 0 0 0 0'};
        ${({ hasLabel, iconRight }) => hasLabel && iconRight && 'margin: 0 0 0 8px'};
        ${({ hasLabel, iconRight }) => hasLabel && !iconRight && 'margin: 0 8px 0 0'};
    }

    ${({ size }) => size === 's' && 'font-size: 12px;'};
`;

const Label = styled.span<{ size: JRCButtonProps['size'] }>`
    ${({ size }) => size === 's' && `font-size: ${SIZES.s}px;`};
`;

const JRCButtonElement = (
    {
        color = 'primary',
        type = 'button',
        variant = 'contained',
        hasLicense = true,
        size = 'm',
        themeCustom,
        ...props
    }: JRCButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
) => {
    const intl = useIntl();

    /**
     * Warning: part of the logic is duplicated in JRCHref
     */
    const disabled = props.disabled || props.loader || !hasLicense;
    const themeButton = themeCustom ? themeCustom : BUTTON_CONFIG[variant][disabled ? 'disabled' : color];

    return (
        <JRCConditionalTooltip
            condition={!!props.tooltip}
            hasLicense={hasLicense}
            tooltip={props.tooltip as JRCTooltipProps}>
            <Button
                hasLabel={!!props.children || !!props.label}
                $themeButton={themeButton}
                type={type}
                data-cy={props.dataCy}
                disabled={disabled}
                {...props}
                size={size}
                ref={ref}>
                {props.icon && !props.loader && <JRCIcon name={props.icon} color={themeButton.color} />}
                {props.loader && <JRCLoader color="grey3" />}
                {props.label ? <Label size={size}>{intl.formatMessage({ id: props.label })}</Label> : props.children}
            </Button>
        </JRCConditionalTooltip>
    );
};

export const JRCButton = React.forwardRef(JRCButtonElement);
